import { Badge, Button, Flex, Text } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { EditableTileContainer } from '@dealstack/frontend/shared/components/EditableTileContainer/EditableTileContainer.tsx';
import { CopyIconButton } from '@dealstack/frontend/shared/components/CopyIconButton/CopyIconButton.tsx';
import React, { ReactNode } from 'react';
import {
	IBAN_COMPONENT_ELEMENT_ID,
	TEST_ELEMENT_IDS,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData';
import ReactCountryFlag from 'react-country-flag';
import { maskIdentifier } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import type { BankAccountDetails } from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { getTileIdentifierRows, STATUS_BADGES } from './BankAccountTile.utils.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	FGAInternalRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';

interface Props {
	bankAccount: BankAccountDetails;
	onRemove?: (ba: BankAccountDetails) => void;
	onEdit?: (ba: BankAccountDetails) => void;
	// Debug-only (MIP-2656): renders an untranslated Debug button opening the
	// lifecycle drawer; only admin-app hosts pass it.
	onInspect?: (ba: BankAccountDetails) => void;
	showStatusBadge?: boolean;
	showCopyButton?: boolean;
	hasEditPermission: boolean;
	tileTestId: TEST_ELEMENT_IDS;
	editBtnTestId?: TEST_ELEMENT_IDS;
	deleteBtnTestId?: TEST_ELEMENT_IDS;
	statusBadgeTestId?: TEST_ELEMENT_IDS;
	copyBtnTestId?: TEST_ELEMENT_IDS;
	testIdSuffix?: string;
	withBorder?: boolean;
	backgroundColor?: string;
	customContent?: ReactNode;
}

export const BankAccountTile = ({
	bankAccount,
	onRemove,
	onEdit,
	onInspect,
	showStatusBadge,
	showCopyButton,
	hasEditPermission,
	tileTestId,
	editBtnTestId,
	deleteBtnTestId,
	statusBadgeTestId,
	copyBtnTestId,
	testIdSuffix,
	withBorder,
	backgroundColor,
	customContent,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const { value: bankAccountStatusEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.BANK_ACCOUNT_STATUS
	);
	const { hasPermission: hasSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: '',
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});
	// An IBAN account keeps the classic two lines; a local-format account lists the
	// identifiers it was entered with, labelled by the server.
	const isIbanAccount = !!bankAccount.iban;
	const identifierRows = getTileIdentifierRows(bankAccount.identifiers);

	// Every line falls back to a dash so legacy accounts with empty data (e.g.
	// migrated MIP rows) still render a recognizable tile instead of an empty box.
	const tileTitle = bankAccount.bankName || bankAccount.accountHolder || '-';
	const idSuffix = testIdSuffix ?? tileTitle;

	// Resolving the name is also the validity check: legacy rows can hold no country
	// (unmappable free text was left NULL by the MIP-2542 migration) and an unknown
	// code would render an empty glyph, so no name means no flag.
	const countryCode = bankAccount.countryCode ?? '';
	const countryName = getCountryNameByCode(countryCode);

	const copyValue = [
		bankAccount.accountHolder,
		bankAccount.bankName,
		...(isIbanAccount || identifierRows.length === 0
			? [bankAccount.iban, bankAccount.accountNumber]
			: identifierRows.map((row) => row.value)),
	]
		.filter((part) => part?.trim())
		.join(' · ');

	return (
		<EditableTileContainer
			hasEditPermission={hasEditPermission}
			onEditClick={onEdit ? () => onEdit(bankAccount) : undefined}
			onDeleteClick={onRemove ? () => onRemove(bankAccount) : undefined}
			withBorder={withBorder}
			backgroundColor={backgroundColor}
			customContent={customContent}
			dataTestId={makeComplexDataTestId(tileTestId, idSuffix)}
			deleteBtnDataTestId={
				deleteBtnTestId ? makeComplexDataTestId(deleteBtnTestId, idSuffix) : undefined
			}
			editBtnDataTestId={
				editBtnTestId ? makeComplexDataTestId(editBtnTestId, idSuffix) : undefined
			}
		>
			<Flex w="100%" gap="0.5rem" align="flex-start" justify="space-between">
				<Flex direction="column" gap="0.25rem">
					<Flex align="center" gap="0.5rem">
						<Text fw={600} c="dark.8">
							{tileTitle}
						</Text>
						{countryName && (
							<ReactCountryFlag
								countryCode={countryCode}
								svg
								title={countryName}
								aria-label={countryName}
								data-testid={makeComplexDataTestId(
									IBAN_COMPONENT_ELEMENT_ID.BANK_ACCOUNT_TILE_COUNTRY_FLAG,
									countryCode
								)}
							/>
						)}
					</Flex>
					{!!bankAccount.bankName && (
						<Text fw={400} c="dark.0">
							{bankAccount.accountHolder}
						</Text>
					)}
					{isIbanAccount || identifierRows.length === 0 ? (
						<>
							<Text fw={400} c="dark.0">
								{`${tCommonUi('bank_account_tile.account_number_label')}: ${maskIdentifier('account_number', bankAccount.accountNumber) || '-'}`}
							</Text>
							<Text fw={400} c="dark.0">
								{`${tCommonUi('bank_account_tile.iban_label')}: ${maskIdentifier('iban', bankAccount.iban) || '-'}`}
							</Text>
						</>
					) : (
						identifierRows.map((row) => (
							<Text key={row.key} fw={400} c="dark.0">
								{row.label}: {maskIdentifier(row.key, row.value) || '-'}
							</Text>
						))
					)}
					{showStatusBadge && bankAccountStatusEnabled && (
						<Flex align="center" gap="0.5rem" wrap="wrap">
							{bankAccount.status && STATUS_BADGES[bankAccount.status] ? (
								<Badge
									color={STATUS_BADGES[bankAccount.status].color}
									c={
										STATUS_BADGES[bankAccount.status].color === 'gray'
											? 'dark.8'
											: undefined
									}
									variant="outline"
									size="sm"
									fw={400}
									styles={{ root: { overflow: 'visible', flexShrink: 0 } }}
									data-testid={
										statusBadgeTestId
											? makeComplexDataTestId(
													statusBadgeTestId,
													bankAccount.status
												)
											: undefined
									}
								>
									{tCommonUi(STATUS_BADGES[bankAccount.status].labelKey)}
								</Badge>
							) : null}
							{onInspect && hasSupportPermission ? (
								<Button
									variant="transparent"
									size="sm"
									onClick={() => onInspect(bankAccount)}
								>
									Debug
								</Button>
							) : null}
						</Flex>
					)}
				</Flex>
				{showCopyButton && (
					<CopyIconButton
						value={copyValue}
						dataTestId={
							copyBtnTestId
								? makeComplexDataTestId(copyBtnTestId, idSuffix)
								: undefined
						}
					/>
				)}
			</Flex>
		</EditableTileContainer>
	);
};
